import { httpsCallable } from 'firebase/functions';
import { useEffect, useMemo, useState } from 'react';
import type {
  ListParticipantsInput,
  ListParticipantsResult,
  ParticipantSummary,
} from '@citizen-walk/shared-types';
import { buildPersonIdMap } from '@citizen-walk/shared-types';
import { functions } from '../../lib/firebase';

/**
 * アンケート回答の出力に付ける「同一人物ID」の対応表 (2026-09-18、docs/98 R-99)。
 *
 * membership は本人しか直接読めない (firestore.rules) ため、admin callable `listParticipants`
 * の結果から作る。畳み込みはサーバ側のエクスポートと同じ `buildPersonIdMap` (R-94 の連結成分)。
 *
 * 注: 写真まとめて一括投稿 feature の `useListParticipants` と同じ callable を叩くが、
 * そちらを import すると地図ライブラリまで読み込む連鎖になる (テスト環境で落ちる) ため、
 * ここでは callable を直接呼ぶ。
 */
export interface SurveyPersonIds {
  /** uid → 同一人物ID。取得できないときは空 (呼出側は uid をそのまま使う)。 */
  personIdByUid: Map<string, string>;
  /** 取得に失敗したか (画面に「uid のまま出す」旨を出すため)。 */
  unavailable: boolean;
  /** uid → 参加者の今の登録内容 (回答に記録されていない回答者情報を補う、respondentProfile.ts)。 */
  participantByUid: Map<string, ParticipantSummary>;
}

export function useSurveyPersonIds(districtId: string | null): SurveyPersonIds {
  const [participants, setParticipants] = useState<ListParticipantsResult['participants'] | null>(
    null,
  );
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    if (!districtId) return;
    let cancelled = false;
    const fn = httpsCallable<ListParticipantsInput, ListParticipantsResult>(
      functions,
      'listParticipants',
    );
    fn({ districtId })
      .then((res) => {
        if (cancelled) return;
        setParticipants(res.data.participants ?? []);
        setUnavailable(false);
      })
      .catch(() => {
        if (!cancelled) setUnavailable(true);
      });
    return () => {
      cancelled = true;
    };
  }, [districtId]);

  const personIdByUid = useMemo(() => {
    if (!participants) return new Map<string, string>();
    const map = buildPersonIdMap(
      participants.map((p) => ({
        uid: p.uid,
        linkedFromUids: p.linkedFromUids ?? [],
        lastSeenAtMs: p.lastSeenAt ? Date.parse(p.lastSeenAt) : null,
      })),
    );
    return new Map([...map].map(([uid, e]) => [uid, e.personId]));
  }, [participants]);

  const participantByUid = useMemo(
    () => new Map((participants ?? []).map((p) => [p.uid, p])),
    [participants],
  );

  return { personIdByUid, unavailable, participantByUid };
}
